import { useSuspenseQuery } from '@tanstack/react-query';
import type * as React from 'react';

import type { SiteRedirectDestination } from '@gitbook/api-client';
import { useHiveClient } from '@gitbook/hive-react';
import { type IconDefinition, IconSkeleton, renderIcon, TextSkeleton } from '@gitbook/spine-react';

import { apiQuery, useSiteContext } from '../../../../../state';
import { getPropsFromResolvedDestination, siteRedirectDestinationResolutionQuery } from './util';

export function SiteRedirectDestinationLabel(props: { destination: SiteRedirectDestination }) {
    const { destination } = props;
    const hiveClient = useHiveClient();
    const { site, organizationId } = useSiteContext();
    const { data: siteStructure } = useSuspenseQuery(
        apiQuery('getSiteStructure', { organizationId, siteId: site.id })
    );
    const { data } = useSuspenseQuery(
        siteRedirectDestinationResolutionQuery({
            hiveClient,
            siteRedirectDestination: destination,
            siteStructure,
        })
    );

    // In some cases, the destination may not be resolvable, so we need to handle that case.
    if (!data) {
        return <>Unknown content</>;
    }

    const { label, leadingIcon } = getPropsFromResolvedDestination(data);

    return <SiteRedirectDestinationLabelTemplate icon={leadingIcon} label={label} />;
}

export function SiteRedirectDestinationLabelSkeleton() {
    return (
        <SiteRedirectDestinationLabelTemplate
            icon={<IconSkeleton />}
            label={<TextSkeleton>Destination</TextSkeleton>}
        />
    );
}

function SiteRedirectDestinationLabelTemplate(props: {
    icon: IconDefinition;
    label: React.ReactNode;
}) {
    return (
        <div className="inline-flex flex-wrap items-center gap-2">
            {renderIcon(props.icon, { size: 16 })}
            {props.label}
        </div>
    );
}
